import { Component, useEffect, useState, type ReactNode } from 'react';
import { live } from '../diag/monitor.ts';
import { diag, logError, recentEntries } from '../diag/log.ts';
import { buildReport, compact, summarise, type DiagSummary } from '../diag/report.ts';
import { allEntries, clearEntries } from '../diag/store.ts';
import { APP_BUILD } from '../lib/feedback.ts';
import { saveFile } from '../lib/files.ts';
import { useEvents, useRepo, useSyncStatus } from '../lib/hooks.ts';
import { Button, Card } from './ui.tsx';

const OVERLAY_KEY = 'repwise.diag.overlay';
/** In memory first (storage can be unavailable), remembered in localStorage when it works. */
let overlayMem: boolean | null = null;
const overlayOn = () => {
  if (overlayMem !== null) return overlayMem;
  try {
    return localStorage.getItem(OVERLAY_KEY) === '1';
  } catch {
    return false;
  }
};
const fmtMs = (ms: number) => (ms >= 1000 ? `${(ms / 1000).toFixed(1)} s` : `${Math.round(ms)} ms`);
const when = (t?: number) => (t ? new Date(t).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '–');

/** Settings → Diagnostics (ADR-0024): what's recorded on this phone, and a report to share. Works offline. */
export function DiagnosticsPage() {
  const repo = useRepo();
  const events = useEvents();
  const status = useSyncStatus();
  const [sum, setSum] = useState<DiagSummary | null>(null);
  const [state, setState] = useState<'idle' | 'busy' | 'shared' | 'downloaded' | 'sent' | 'failed'>('idle');
  const [taps, setTaps] = useState(0);
  const [overlay, setOverlay] = useState(overlayOn);
  const refresh = () => void allEntries().then((e) => setSum(summarise(e)));
  useEffect(refresh, []);
  const share = async () => {
    setState('busy');
    try {
      const r = await buildReport({ build: APP_BUILD, events, status });
      const how = await saveFile(JSON.stringify(r), `repwise-diagnostics-${new Date().toISOString().slice(0, 16).replace(':', '')}.json`, 'application/json', 'Repwise diagnostics');
      diag('diag', 'report_shared', { how, entries: r.entries.length });
      setState(how);
    } catch (e) {
      if ((e as Error)?.name === 'AbortError') return setState('idle'); // closed the share sheet
      logError('diag.share', e);
      setState('failed');
    }
  };
  const send = async () => {
    if (!repo.sendFeedback) return;
    setState('busy');
    const r = await buildReport({ build: APP_BUILD, events, status });
    const res = await repo.sendFeedback('bug', `Diagnostic report: ${r.summary.hangs} hangs, ${r.summary.errors} errors`, { diagnostics: compact(r) });
    setState(res.ok ? 'sent' : 'failed');
  };
  const tapBuild = () => {
    const n = taps + 1;
    setTaps(n);
    if (n >= 5) {
      const on = !overlay;
      overlayMem = on;
      try {
        on ? localStorage.setItem(OVERLAY_KEY, '1') : localStorage.removeItem(OVERLAY_KEY);
      } catch {
        /* storage unavailable */
      }
      setOverlay(on);
      window.dispatchEvent(new Event('repwise:overlay'));
      setTaps(0);
    }
  };
  return (
    <div className="space-y-4">
      <p>Repwise keeps a small log on this phone: app starts, sync attempts, slow moments, freezes and errors. It holds no notes, names or email, and stays here unless you share it.</p>
      <Card>
        <h2 className="text-headline">On this phone</h2>
        {sum ? (
          <ul className="text-sm mt-2 space-y-2 [&>li]:bg-surface-sunken [&>li]:rounded-[10px] [&>li]:px-3 [&>li]:py-2" aria-label="Diagnostics summary">
            <li>
              {sum.hangs} freeze{sum.hangs === 1 ? '' : 's'}
              {sum.hangs ? `, longest ${fmtMs(sum.longestHangMs)}` : ''}
            </li>
            <li>
              {sum.errors} error{sum.errors === 1 ? '' : 's'} · {sum.slow} slow step{sum.slow === 1 ? '' : 's'}
            </li>
            {sum.unexpectedEnds > 0 && <li>{sum.unexpectedEnds} time{sum.unexpectedEnds === 1 ? '' : 's'} the app was closed while it was busy</li>}
            <li className="text-text-muted">
              {sum.entries} entries, {when(sum.from)} to {when(sum.to)}
            </li>
          </ul>
        ) : (
          <p className="text-sm text-text-muted">Loading…</p>
        )}
      </Card>
      <Button disabled={state === 'busy'} onClick={() => void share()}>
        Share a diagnostic report
      </Button>
      {repo.sendFeedback && (
        <Button kind="secondary" disabled={state === 'busy'} onClick={() => void send()}>
          Send a summary to Repwise
        </Button>
      )}
      {state !== 'idle' && state !== 'busy' && (
        <p role="status" className="text-sm">
          {state === 'shared' ? 'Shared.' : state === 'downloaded' ? 'Saved to your downloads.' : state === 'sent' ? 'Sent. Thank you.' : "That didn't work. Try sharing the file instead."}
        </p>
      )}
      <Button kind="text" onClick={() => void clearEntries().then(refresh)}>
        Clear the log
      </Button>
      <button type="button" className="block text-xs text-text-muted min-h-11" onClick={tapBuild} aria-label={`Build ${APP_BUILD}`}>
        Build {APP_BUILD}
        {overlay ? ' · debug panel on' : ''}
      </button>
    </div>
  );
}

/** Tap the build number five times: a live panel for airplane-mode runs (lag, last freeze, log size, sync). */
export function DebugOverlay() {
  const [on, setOn] = useState(overlayOn);
  const [, tick] = useState(0);
  const status = useSyncStatus();
  useEffect(() => {
    const f = () => setOn(overlayOn());
    window.addEventListener('repwise:overlay', f);
    const t = setInterval(() => tick((x) => x + 1), 1000);
    return () => (window.removeEventListener('repwise:overlay', f), clearInterval(t));
  }, []);
  if (!on) return null;
  const lastHang = [...recentEntries].reverse().find((e) => e.area === 'hang' && e.event === 'end');
  const last = recentEntries.at(-1);
  return (
    <div className="fixed top-1 right-1 z-50 bg-black/80 text-white text-[10px] leading-tight rounded p-1.5 num pointer-events-none max-w-[60vw]" aria-hidden>
      <div>
        lag {live.lagMs} ms · max {live.maxLagMs} · {live.watchdog ? 'dog ✓' : 'dog ✗'}
      </div>
      <div>last freeze {lastHang ? fmtMs(Number(lastHang.data?.ms ?? 0)) : 'none'}</div>
      <div>{status ? `${status.label} · ${status.pending} pending` : ''}</div>
      <div className="truncate">{last ? `${last.area}.${last.event}` : ''}</div>
    </div>
  );
}

/** A crash in a screen shows a way out instead of a blank page, and is logged with the component stack. */
export class ErrorBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  override state = { failed: false };
  static getDerivedStateFromError() {
    return { failed: true };
  }
  override componentDidCatch(error: unknown, info: { componentStack?: string | null }) {
    logError('render', error);
    diag('error', 'render_stack', { stack: (info.componentStack ?? '').split('\n').slice(0, 8).join(' | ') }, 'error');
  }
  override render() {
    if (!this.state.failed) return this.props.children;
    return (
      <main className="min-h-dvh p-5 max-w-[480px] mx-auto flex flex-col justify-center" role="alert">
        {/* ER01 `_crash` final (Wave 2.4 batch 4) */}
        <div className="bg-surface-raised rounded-[20px] p-6 space-y-3">
          <span className="min-w-11 min-h-11 rounded-[12px] bg-surface-sunken grid place-items-center text-sync-warn font-bold" aria-hidden="true">
            !
          </span>
          <h1 className="text-title">Something went wrong</h1>
          <p className="text-text-muted">Your workouts are safe on this phone. Reloading usually fixes it.</p>
          <Button onClick={() => window.location.reload()}>Reload</Button>
          <a href="/settings/diagnostics" className="block text-center text-now-ink underline min-h-11 pt-3">
            Share a diagnostic report
          </a>
        </div>
      </main>
    );
  }
}
